import { useState } from 'react';
import { SettingsModal } from '../SettingsModal';
import { validateCsvCallback, validateImageOrPdfCallback } from '../../../../utils/validateFiles';
import { IadditionalServiceDataForToolLogistics } from '../../../../types/product.configuration.types';
import { getAdditionalServiceDisplayElements } from '../../../../utils/logisticServices.utils';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../utils/displayNudosStandardNotifications';
import { updateAdditionalServiceStatus } from '../../../../services/services';
import { NudosLinkTypeButton, NudosUploadDownloadButton } from '../../../NudosComponents';
import NudosRadioButton from '../../../NudosRadioButton/NudosRadioButton';
import {
  ADDITIONAL_SERVICE_CLASS_TYPE,
  ADDITIONAL_SERVICE_STATUS_MODAL_VALUES
} from '../../../../types/product.configuration.types';
import AdditionalServiceStatusModal from '../AdditionalServiceStatusModal/AdditionalServiceStatusModal';
import EnrollmentStatusIndicator from './EnrollmentStatusIndicator/EnrollmentStatusIndicator';
import { CONFIG_ADDITIONAL_ACTION_ID, CONFIG_STATUS_NAME } from '../../../../types/product.configuration.types';
import { getConfigClass, getAdditionalAction } from '../../../../utils/configurations';
import { ResellerSearchBar } from './ResellerSearchBar/ResellerSearchBar';

import './AdditionalServiceOrConfigurationStatusSection.scss';

const AdditionalServiceOrConfigurationStatusSection = ({
  additionalServiceData,
  updateDataCallback,
  isConfiguration,
  countryId,
  countryCode,
  orderId
}: {
  additionalServiceData: IadditionalServiceDataForToolLogistics;
  updateDataCallback: () => Promise<void>;
  isConfiguration?: boolean;
  countryId?: number | null;
  countryCode?: string | null;
  orderId?: string | null;
}) => {
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [changeStatusModalType, setChangeStatusModalType] = useState<ADDITIONAL_SERVICE_STATUS_MODAL_VALUES>();
  const [loadingProofFile, setLoadingProofFile] = useState<boolean>(false);

  const {
    uuid: productUuid,
    status: currentStatus,
    comments,
    configData,
    configClassId,
    id: logisticAdditionalServiceProductId,
    additionalActions,
    configClassName,
    reseller
  } = additionalServiceData;

  const {
    statusId,
    statusName,
    notCompletedReasonId,
    otherReasonComment,
    configurationImageUrls,
    isExternalService,
    imageUrl: additionalServiceImageUrl
  } = currentStatus || {};

  const statusIsNotCompleted = statusName === CONFIG_STATUS_NAME.NOT_COMPLETED;

  const configurationIsHashId = configClassName === ADDITIONAL_SERVICE_CLASS_TYPE.HASH_ID;
  const configurationIsCreateAdmin = configClassName === ADDITIONAL_SERVICE_CLASS_TYPE.CREATE_ADMIN;
  const isDEPLaptopsCategory = configClassName === ADDITIONAL_SERVICE_CLASS_TYPE.DEP;

  const getImageUrlsToShow = () => {
    if (isExternalService) return [];
    if (additionalServiceImageUrl && !configurationImageUrls?.length) return [additionalServiceImageUrl];
    if (configurationIsHashId && !statusIsNotCompleted) return configurationImageUrls?.slice(-2);
    return configurationImageUrls?.slice(-1);
  };

  const imageUrlToShow = getImageUrlsToShow();
  const { text } = getAdditionalServiceDisplayElements(additionalServiceData);

  const additionalServiceStatuses = [
    {
      statusName: CONFIG_STATUS_NAME.TO_COMPLETE,
      isActive: currentStatus?.statusName === CONFIG_STATUS_NAME.TO_COMPLETE,
      clickAction: undefined
    },
    {
      statusName: CONFIG_STATUS_NAME.COMPLETED,
      isActive: currentStatus?.statusName === CONFIG_STATUS_NAME.COMPLETED,
      clickAction: () =>
        !isExternalService && setChangeStatusModalType(ADDITIONAL_SERVICE_STATUS_MODAL_VALUES.COMPLETED)
    },
    {
      statusName: CONFIG_STATUS_NAME.NOT_COMPLETED,
      isActive: currentStatus?.statusName === CONFIG_STATUS_NAME.NOT_COMPLETED,
      clickAction: () =>
        !isExternalService && setChangeStatusModalType(ADDITIONAL_SERVICE_STATUS_MODAL_VALUES.NOT_COMPLETED)
    }
  ];

  const enrollmentAction = getAdditionalAction(additionalActions, CONFIG_ADDITIONAL_ACTION_ID.CONFIRM_ENROLLMENT);

  const openCommentsModal = () => setShowCommentsModal(true);
  const closeCommentsModal = () => setShowCommentsModal(false);
  const closeChangeStatusModal = () => setChangeStatusModalType(undefined);

  const uploadProofFile = async (file?: File) => {
    if (!logisticAdditionalServiceProductId || !statusId)
      return displayErrorNotification({
        customJSXMessage: <>Funcionalidad aún no implementada para órdenes, solo para SL</>
      });
    if (!file) {
      return displayErrorNotification({
        customJSXMessage: <>No se cargó ninguna imagen</>
      });
    }
    if (statusName === CONFIG_STATUS_NAME.NOT_COMPLETED && !notCompletedReasonId) return displayErrorNotification();
    try {
      setLoadingProofFile(true);
      const body = {
        logisticAdditionalServiceProductId,
        configStatusId: statusId,
        logisticAdditionalServiceNotCompletedReasonId: notCompletedReasonId || undefined,
        otherReasonComment: otherReasonComment || undefined,
        imageUrl: undefined
      };
      await updateAdditionalServiceStatus(body, file);
      await updateDataCallback();
      displaySuccessNotification({
        customJSXMessage: <>{`${isConfiguration ? 'Configuración' : 'Servicio'} adicional actualizado con éxito`}</>
      });
    } catch {
      displayErrorNotification({
        customJSXMessage: <>{`Error al actualizar ${isConfiguration ? 'la configuración' : 'el servicio'} adicional`}</>
      });
    } finally {
      setLoadingProofFile(false);
    }
  };

  return (
    <div className="additionalServiceOrConfigurationStatusSection">
      <div className="nameAndComments">
        <div className="serviceName">{text}</div>
        {comments && <NudosLinkTypeButton hideIcon text="Ver configuración" handleClickCallback={openCommentsModal} />}
      </div>
      <div className="statuses">
        {additionalServiceStatuses.map(additionaServiceStatus => {
          const { clickAction, statusName, isActive } = additionaServiceStatus;
          const isNotCompleted = statusName === CONFIG_STATUS_NAME.NOT_COMPLETED;
          const isCompleted = statusName === CONFIG_STATUS_NAME.COMPLETED;
          const isPending = statusName === CONFIG_STATUS_NAME.TO_COMPLETE;
          const externalStyle = isExternalService ? 'external' : '';
          const disabledStyle = isPending || isActive ? 'disabled' : '';
          const notCompletedStyle = isNotCompleted ? `notCompleted notCompletedConfig` : '';
          const displayViewReasonsButton = (isNotCompleted || (isCompleted && configurationIsCreateAdmin)) && isActive;
          const seeNotCompletedReasons = () => {
            if (isNotCompleted) {
              return setChangeStatusModalType(ADDITIONAL_SERVICE_STATUS_MODAL_VALUES.SEE_NOT_COMPLETED_REASONS);
            }
            if (configurationIsCreateAdmin) {
              return setChangeStatusModalType(ADDITIONAL_SERVICE_STATUS_MODAL_VALUES.COMPLETED);
            }
          };
          return (
            <div className={`statusIndicator ${externalStyle} ${notCompletedStyle} ${disabledStyle}`} key={statusName}>
              <NudosRadioButton
                componentSize="small"
                isButtonActive={isActive}
                handleClickButton={isActive ? undefined : clickAction}
              />
              <div className="statusName">{statusName}</div>
              {displayViewReasonsButton && (
                <NudosLinkTypeButton hideIcon text={'Ver motivos'} handleClickCallback={seeNotCompletedReasons} />
              )}
            </div>
          );
        })}
      </div>

      <div className="evidenceContainer">
        {imageUrlToShow?.map((imageUrl, index) => {
          const fileExtension = imageUrl?.split('.').pop()?.toLowerCase();
          const isCsv = fileExtension === 'csv';
          const buttonText = isCsv ? 'CSV' : 'PDF o JPG';
          const validateFileCallback = isCsv ? validateCsvCallback : validateImageOrPdfCallback;
          return (
            <NudosUploadDownloadButton
              key={`imageUrl-${index}`}
              customClassName={`updateProofButton ${isConfiguration ? 'updateProofButtonOrders' : ''}`}
              customWidth="127px"
              customHeight="30px"
              currentFileUrl={imageUrl}
              uploadedFileHandler={uploadProofFile}
              downloadButtonText="Ver evidencia"
              buttonEmptyText={buttonText}
              buttonFilledText={buttonText}
              showSkeletonLoader={loadingProofFile}
              keepOwnFileState
              validateFileCallback={validateFileCallback}
            />
          );
        })}
      </div>

      {enrollmentAction && isConfiguration && (
        <EnrollmentStatusIndicator enrollmentAction={enrollmentAction} onConfirmEnrollment={updateDataCallback} />
      )}

      {isDEPLaptopsCategory && isConfiguration && (
        <ResellerSearchBar
          reseller={reseller}
          countryCode={countryCode}
          countryId={countryId}
          productUuid={productUuid}
          onResellerSelect={updateDataCallback}
        />
      )}

      {showCommentsModal && (
        <SettingsModal
          closeModal={closeCommentsModal}
          configClass={getConfigClass(configClassId)}
          configData={configData || []}
          textDetails={comments || ''}
          isConfiguration={isConfiguration}
        />
      )}
      {changeStatusModalType && (
        <AdditionalServiceStatusModal
          type={changeStatusModalType}
          closeModalCallback={closeChangeStatusModal}
          updateDataCallback={updateDataCallback}
          additionalServiceData={additionalServiceData}
          isConfiguration={isConfiguration}
          orderId={orderId}
        />
      )}
    </div>
  );
};

export default AdditionalServiceOrConfigurationStatusSection;
